// src/utils/bitacorasQuery.js
// Construcción de la query string de la consulta de bitácoras.
// Extraído de Bitacoras.js para poder probarlo sin montar el formulario.

// Escritura exacta según el Manual Web API SFTF V1.1, sección 1.
// El orden es el del manual y determina el orden de la query string.
export const CAMPOS_FILTRO_CONSULTA = [
  'ClaveBitacora',
  'SerieBitacora',
  'FolioBitacora',
  'SerieRemision',
  'FolioRemision',
  'ConTimbre',
  'Estatus',
];

const tieneValor = (valor) =>
  valor !== undefined && valor !== null && String(valor).trim() !== '';

/**
 * Arma la query string con los filtros que el usuario llenó.
 *
 * @param {Object} values Valores del formulario de búsqueda.
 * @returns {String} Query string sin el '?' inicial.
 */
export const construirParamsConsulta = (values = {}) =>
  CAMPOS_FILTRO_CONSULTA
    .filter((campo) => tieneValor(values[campo]))
    .map((campo) => `${campo}=${encodeURIComponent(String(values[campo]).trim())}`)
    .join('&');

// Filtros que hoy fallan del lado de Cryoinfra sin importar con qué se
// combinen (ver docs/soporte/2026-09-24-gaps-manual-sftf-v1.1.md):
// ClaveBitacora truena con Bad Request, Estatus y los dos de remisión con
// timeout. SerieBitacora no está aquí porque solo falla cuando viaja sin
// FolioBitacora (se resuelve aparte, más abajo).
const CAMPOS_SIN_SOPORTE_SIEMPRE = ['ClaveBitacora', 'SerieRemision', 'FolioRemision', 'Estatus'];

/**
 * Indica si la consulta se apoya únicamente en filtros que el hub de
 * Cryoinfra no responde hoy: ClaveBitacora, Estatus, SerieRemision,
 * FolioRemision, y SerieBitacora sin FolioBitacora. FolioBitacora y ConTimbre
 * sí funcionan (solos o combinados), así que su presencia descarta este caso.
 *
 * Antes esto solo cubría remisión (ver el helper `consultaSoloPorRemision`
 * que reemplaza). Se generaliza para que ClaveBitacora, Estatus y
 * SerieBitacora sin folio entren "con la misma lógica honesta acordada para
 * remisión" (spec, Etapa 3): con los reintentos por defecto (3 intentos,
 * corte a 45 s) cualquiera de estas búsquedas tarda entre 65 y 140 s en
 * fallar. Se usa para bajar a un solo intento y que el error aparezca en
 * ~20-30 s.
 *
 * @param {Object} values Valores del formulario de búsqueda.
 * @returns {Boolean}
 */
export const consultaSoloPorFiltrosNoSoportados = (values = {}) => {
  const filtrosProvistos = CAMPOS_FILTRO_CONSULTA.filter((campo) => tieneValor(values[campo]));
  if (filtrosProvistos.length === 0) return false;

  return filtrosProvistos.every((campo) => {
    if (campo === 'FolioBitacora' || campo === 'ConTimbre') return false;
    if (campo === 'SerieBitacora') return !tieneValor(values.FolioBitacora);
    return CAMPOS_SIN_SOPORTE_SIEMPRE.includes(campo);
  });
};

/**
 * Indica si un registro de bitácora ya tiene una factura asociada, sin
 * importar si llegó por la consulta con timbre (ConTimbre=true, esquema con
 * ClaveFactura/UUID/SerieFactura/FolioFactura) o por cualquier otra
 * combinación de filtros que termine mezclando esquemas.
 *
 * Se usa para que las acciones de la tabla de Bitácoras (Crear Adenda,
 * Ingresar Factura) nunca queden disponibles sobre un registro ya facturado:
 * el guardia anterior (`ClaveEstatus === 11`) no existe en el esquema con
 * timbre, así que un registro ya facturado pasaba colado con ambos botones
 * activos — el de "Ingresar Factura" prefillaba el formulario con los datos
 * de la factura que ya existe, abriendo la puerta a un CFDI duplicado.
 *
 * @param {Object} registro
 * @returns {Boolean}
 */
export const esRegistroYaFacturado = (registro) =>
  Boolean(registro?.UUID) || Boolean(registro?.ClaveFactura);
